import { SLOT_MS } from "./clock.js";
import type { PreparedNarration } from "../audio/narrator.js";

/**
 * Lead-slot preparation scheduler.
 *
 * The prior design (a one-slot lookahead triggered at
 * `elapsed > max(estimate, 30s)`) could never fire once the estimate reached a
 * full slot, so no future work ever started and silence became permanent. This
 * version has a single retargeting worker instead.
 *
 * Guarantees:
 *   - Exactly one preparation runs at a time. kokoro-js generation accepts no
 *     AbortSignal, so logical cancellation cannot stop inference; bounding
 *     concurrency is what keeps resources predictable and prevents a FIFO of
 *     stale work.
 *   - The target is computed from the current instant, not the slot boundary:
 *     `targetSlot = ceil((now + estimate + margin) / SLOT_MS)`. A worker that
 *     frees up 59 s into a slot would otherwise get 59 s less lead than the
 *     estimate requires and miss every time.
 *   - The estimator is the running max of the last five completed prepares,
 *     including late ones. An estimator that under-reports is what produces
 *     misses; there is no clamp below observed reality.
 *   - The worker re-arms on every completion, failure, and boundary.
 *   - A buffer prepared for a past slot is always discarded. There is no
 *     late-play mode: wall-clock alignment always wins.
 */

export interface SchedulerDeps {
  now: () => number;
  prepare: (slotIndex: number, signal: AbortSignal) => Promise<PreparedNarration | null>;
  onStatus?: (status: SchedulerStatus) => void;
  onReady?: (prepared: PreparedNarration) => void;
  slotMs?: number;
  marginMs?: number;
  historySize?: number;
  initialEstimateMs?: number;
  estimateFloorMs?: number;
  /**
   * Aim the very first preparation at the current slot.
   *
   * Before any duration has been observed the estimate is a guess, and a guess
   * that crosses a boundary aim the first prepare at the next slot, which can
   * delay the first spoken disclosure by almost a minute even when the
   * inference takes seconds. A first attempt that lands too late is discarded
   * by the existing staleness rule, so the only cost is one wasted inference.
   */
  firstPrepareTargetsCurrent?: boolean;
  /** Wait before re-attempting after a failure or an empty result. */
  retryFloorMs?: number;
  /**
   * How many slots ahead of the target the single worker may fill.
   *
   * Inference is slow enough that the full window is rarely reached, but a
   * cache prepare is fast: without a bound, one hit would download the whole
   * lookahead at once. Cache mode passes a smaller number.
   */
  lookaheadSlots?: number;
  /** Injectable timer, so tests do not wait wall-clock time for a retry. */
  scheduleTimer?: (callback: () => void, delayMs: number) => number;
  /** Injectable cancel, paired with `scheduleTimer`. */
  cancelTimer?: (handle: number) => void;
  /**
   * How long a single preparation may run before it is presumed wedged.
   *
   * Defaults to four times the current estimate with a 3-minute floor, so a
   * genuinely slow-but-progressing prepare (the accepted 400 s case) is never
   * killed while a promise that will never settle is caught. Without this, a
   * hung inference permanently occupies the sole worker: every later pump
   * returns immediately at `inFlight`, no future slot is ever prepared, and
   * narration dead air becomes permanent.
   */
  stuckTimeoutMs?: (estimateMs: number) => number;
  /**
   * Recovery hook, invoked once when a preparation exceeds its deadline.
   *
   * Its job is to replace whatever wedged — for Kokoro, a fresh pipeline over
   * the same cached weights. It must resolve to report a usable narrator, or
   * reject/throw to report that recovery failed and the worker stays held.
   */
  recoverStuck?: () => Promise<void>;
  /** Bound on how many times the watchdog will call `recoverStuck`. */
  maxRecoveries?: number;
}

export interface SchedulerStatus {
  estimateMs: number;
  lastDurationMs: number | null;
  currentSlot: number;
  targetSlot: number;
  leadSlots: number;
  inFlightSlot: number | null;
  readySlots: number[];
  degraded: boolean;
  /** A preparation has exceeded its deadline and recovery is in progress. */
  stuck?: boolean;
}

/**
 * What one preparation costs, by backend, until real durations replace the guess.
 *
 * Measured through this loader: about 2-4 s per prepare on WebGPU with the
 * full-precision artifact, and roughly 30 s on WASM with the quantized one.
 * Using the WASM figure everywhere is what made the first spoken disclosure
 * wait for a boundary on machines that take two seconds.
 */
/** Bounded lookahead so the ready map cannot grow without limit. */
const LOOKAHEAD_SLOTS = 8;

/** Hard upper bound on how far ahead a ready buffer may be retained. */
const READY_HORIZON_SLOTS = 64;

/**
 * Default wedged-preparation deadline.
 *
 * Four times the current estimate, floored at ten minutes.
 *
 * The floor matters more than the multiplier. A fresh station's estimate starts
 * at 15 s, so four times it is 60 s — which would kill the very first, legitimate
 * model-backed prepare. The station's documented accepted cost for a genuinely
 * slow prepare is measured in hundreds of seconds (it degrades to music-only
 * rather than playing stale audio), so the floor sits above that with margin: a
 * slow station is never mistaken for a hung one, and only a promise that will
 * never settle reaches the deadline at all. Once real durations are observed,
 * four times the running maximum takes over and scales with whatever this device
 * actually costs.
 */
export function defaultStuckTimeoutMs(estimateMs: number): number {
  return Math.max(SLOW_PREPARE_DEADLINE_MS, estimateMs * 4);
}

/** A prepare is not treated as wedged until it has exceeded this. */
const SLOW_PREPARE_DEADLINE_MS = 10 * 60_000;

export class PreparationScheduler {
  private readonly slotMs: number;
  private readonly marginMs: number;
  private readonly historySize: number;
  private readonly estimateFloorMs: number;
  private readonly durationHistory: number[] = [];
  private inFlight: { slotIndex: number; controller: AbortController; token: symbol } | null = null;
  private readonly ready = new Map<number, PreparedNarration>();
  private lastDurationMs: number | null = null;
  private disposed = false;
  private estimateOverride: number | null = null;
  private retryTimer: number | null = null;
  private watchdogTimer: number | null = null;
  private stuck = false;
  private recoveries = 0;
  private readonly retryFloorMs: number;
  private readonly maxRecoveries: number;
  private readonly lookaheadSlots: number;
  private readonly scheduleTimer: (callback: () => void, delayMs: number) => number;
  private readonly cancelTimer: (handle: number) => void;

  constructor(private readonly deps: SchedulerDeps) {
    this.slotMs = deps.slotMs ?? SLOT_MS;
    this.marginMs = deps.marginMs ?? 3000;
    this.historySize = deps.historySize ?? 5;
    this.estimateFloorMs = deps.initialEstimateMs ?? 15_000;
    this.estimateOverride = deps.estimateFloorMs ?? null;
    this.retryFloorMs = deps.retryFloorMs ?? 15_000;
    this.maxRecoveries = deps.maxRecoveries ?? 3;
    this.lookaheadSlots = deps.lookaheadSlots ?? LOOKAHEAD_SLOTS;
    this.scheduleTimer =
      deps.scheduleTimer ?? ((callback, delayMs) => setTimeout(callback, delayMs) as unknown as number);
    this.cancelTimer =
      deps.cancelTimer ??
      ((handle) => clearTimeout(handle as unknown as ReturnType<typeof setTimeout>));
  }

  currentSlot(at: number = this.deps.now()): number {
    return Math.floor(at / this.slotMs);
  }

  /** Running max of recent durations. Never clamped below observed reality. */
  get estimateMs(): number {
    if (this.durationHistory.length === 0) return this.estimateOverride ?? this.estimateFloorMs;
    const recent = this.durationHistory.slice(-this.historySize);
    return Math.max(...recent);
  }

  /**
   * The slot the current preparation is aiming at.
   *
   * `floor`, not `ceil`. The requirement is "which slot does the completion
   * instant `now + estimate + margin` land in", and a seekable narrator can
   * join that slot wherever the completion lands. Rounding *up* to the next
   * boundary would mean a listener who joins mid-slot can never receive
   * narration for the slot they are in, which contradicts the whole design.
   *
   * The value is derived from the current instant, not from the slot start: a
   * worker freeing up 59 s into a slot gets 59 s less usable lead, and only a
   * now-relative computation sees that. The clamp keeps it from pointing into
   * the past.
   */
  targetSlot(at: number = this.deps.now()): number {
    const current = this.currentSlot(at);
    const completion = at + this.estimateMs + this.marginMs;
    return Math.max(current, Math.floor(completion / this.slotMs));
  }

  get inFlightSlot(): number | null {
    return this.inFlight?.slotIndex ?? null;
  }

  readySlot(slotIndex: number): PreparedNarration | null {
    return this.ready.get(slotIndex) ?? null;
  }

  status(): SchedulerStatus {
    const at = this.deps.now();
    // Prune before reporting: a status snapshot must never advertise a slot
    // that has already passed, whatever happened since the last boundary.
    this.prunePast(at);
    const current = this.currentSlot(at);
    const target = this.targetSlot(at);
    return {
      estimateMs: this.estimateMs,
      lastDurationMs: this.lastDurationMs,
      currentSlot: current,
      targetSlot: target,
      leadSlots: target - current,
      inFlightSlot: this.inFlightSlot,
      readySlots: [...this.ready.keys()].sort((a, b) => a - b),
      degraded: !this.ready.has(current),
      stuck: this.stuck,
    };
  }

  /** Drop entries whose slot has passed, and anything absurdly far ahead.
   *
   * The lower bound is the hard invariant: a buffer for a slot before the
   * current one must never be served, whatever the estimate says. The upper
   * bound is only a memory guard for a permanently-slow station, so it is
   * generous and must never be tighter than what `pump` can produce. */
  prunePast(at: number = this.deps.now()): void {
    const current = this.currentSlot(at);
    for (const slot of [...this.ready.keys()]) {
      if (slot < current || slot > current + READY_HORIZON_SLOTS) this.ready.delete(slot);
    }
  }

  noteBoundary(): void {
    this.prunePast();
    this.pump();
  }

  /**
   * Start work if the single worker is free and some coverable slot is
   * uncovered. Never queues a backlog.
   *
   * The scan starts at `targetSlot`, not at the current slot. A prepare that
   * cannot possibly finish before the current slot ends would produce a buffer
   * that is already stale by the time it exists, so starting it wastes the whole
   * inference. When the current slot *is* coverable, `targetSlot === current`,
   * so this rule still covers "prepare what can still air".
   *
   * `retryDelayMs` schedules a later attempt instead of starting one now; it is
   * how a failure avoids a zero-time re-arm loop.
   */
  pump(retryDelayMs = 0): void {
    if (this.disposed || this.inFlight) return;
    if (retryDelayMs > 0) {
      if (this.retryTimer !== null) return;
      this.retryTimer = this.scheduleTimer(() => {
        this.retryTimer = null;
        this.pump();
      }, retryDelayMs);
      return;
    }
    const at = this.deps.now();
    this.prunePast(at);
    const firstAttempt = this.deps.firstPrepareTargetsCurrent === true && this.durationHistory.length === 0;
    const target = firstAttempt ? this.currentSlot(at) : this.targetSlot(at);

    let candidate = -1;
    for (let slot = target; slot <= target + this.lookaheadSlots; slot += 1) {
      if (!this.ready.has(slot)) {
        candidate = slot;
        break;
      }
    }
    if (candidate < 0) return;

    const controller = new AbortController();
    const token = Symbol(`prepare:${candidate}`);
    this.inFlight = { slotIndex: candidate, controller, token };
    const startedAt = at;
    let completed = false;
    this.emitStatus();
    this.armWatchdog(token, candidate, startedAt);

    let settled = false;
    const finish = (prepared: PreparedNarration | null, failed: boolean) => {
      if (settled) return;
      settled = true;
      // Only the job that currently owns the worker may touch worker state —
      // including the watchdog. A superseded job's watchdog has already been
      // replaced by the newer job's, and clearing it here would disarm the live
      // job: if that one then wedges, it holds the worker forever, which is
      // exactly the permanent-dead-air failure the watchdog exists to prevent.
      const ownsWorker = this.inFlight?.token === token;
      if (ownsWorker) this.clearWatchdog();
      const finishedAt = this.deps.now();
      const duration = Math.max(0, finishedAt - startedAt);
      if (completed) {
        // Late completions still measure real cost, so they update the estimator.
        this.durationHistory.push(duration);
        while (this.durationHistory.length > this.historySize) this.durationHistory.shift();
        this.lastDurationMs = duration;
        void failed;
      }
      if (ownsWorker) this.inFlight = null;

      const nowSlot = this.currentSlot(finishedAt);
      const stale = controller.signal.aborted || candidate < nowSlot;
      if (!stale && prepared && prepared.slotIndex === candidate) {
        this.ready.set(candidate, prepared);
        this.deps.onReady?.(prepared);
      }

      this.emitStatus();
      if (this.disposed || !ownsWorker) return;

      // Re-arm on completion, failure, and no-op alike. A job that fails or
      // yields nothing must not be retried in a zero-time loop: with no instant
      // work to measure, that would spin forever. Waiting the retry floor keeps
      // the station responsive without burning the CPU.
      const productive = completed && prepared !== null;
      this.pump(productive ? 0 : this.retryFloorMs);
    };

    void (async () => {
      try {
        const prepared = await this.deps.prepare(candidate, controller.signal);
        completed = true;
        finish(prepared, prepared === null);
      } catch {
        finish(null, true);
      }
    })();
  }

  /**
   * A prepare that never settles would hold the worker forever. Nothing in the
   * runtime can cancel inference, so recovery replaces the pipeline instead:
   * the wedged promise is abandoned (it may still settle into a no-op later),
   * `recoverStuck` swaps in a fresh pipeline, and the worker is genuinely freed.
   *
   * This is deliberately the only path that clears `inFlight` without the job
   * settling, because clearing it while the old inference still runs would
   * overlap two inferences — the thing the single-worker rule exists to prevent.
   * Replacement is the honest exception: the old inference is unreachable, so
   * it can never contribute audio again.
   */
  private armWatchdog(token: symbol, slotIndex: number, startedAt: number): void {
    this.clearWatchdog();
    const timeout = (this.deps.stuckTimeoutMs ?? defaultStuckTimeoutMs)(this.estimateMs);
    this.watchdogTimer = this.scheduleTimer(() => {
      this.watchdogTimer = null;
      if (this.disposed) return;
      if (this.inFlight?.token !== token) return;
      void this.recover();
    }, timeout);
    void slotIndex;
    void startedAt;
  }

  private clearWatchdog(): void {
    if (this.watchdogTimer === null) return;
    this.cancelTimer(this.watchdogTimer);
    this.watchdogTimer = null;
  }

  private async recover(): Promise<void> {
    if (this.disposed || !this.inFlight) return;
    const wedged = this.inFlight;
    this.stuck = true;
    this.emitStatus();

    if (this.recoveries >= this.maxRecoveries || !this.deps.recoverStuck) {
      // Recovery is exhausted or unavailable. Stop claiming the slot is
      // preparing and give the worker back so retries can still be attempted;
      // the station stays music-only rather than silently wedged forever.
      this.stuck = false;
      if (this.inFlight?.token === wedged.token) this.inFlight = null;
      this.emitStatus();
      this.pump(this.retryFloorMs);
      return;
    }

    this.recoveries += 1;
    // Logical cancellation: whatever the wedged job returns is discarded.
    try {
      wedged.controller.abort();
    } catch {
      // AbortSignal.throwIfAborted is the only throw path; ignore it.
    }

    try {
      await this.deps.recoverStuck();
    } catch {
      // Recovery failed. Keep the worker held: the pipeline is still unusable,
      // and starting another job against it would only wedge again.
      if (!this.disposed && this.inFlight?.token === wedged.token) {
        this.stuck = false;
        this.inFlight = null;
        this.emitStatus();
        this.pump(this.retryFloorMs);
      }
      return;
    }

    if (this.disposed) return;
    if (this.inFlight?.token === wedged.token) this.inFlight = null;
    this.stuck = false;
    this.emitStatus();
    this.pump();
  }

  /**
   * Mark the in-flight result stale without assuming inference can be killed.
   */
  abandonInFlight(): void {
    this.inFlight?.controller.abort();
  }

  /** Test/diagnostic override for the estimator baseline. */
  setInitialEstimate(ms: number): void {
    this.estimateOverride = ms;
  }

  dispose(): void {
    this.disposed = true;
    this.clearWatchdog();
    this.inFlight?.controller.abort();
    this.inFlight = null;
    if (this.retryTimer !== null) {
      this.cancelTimer(this.retryTimer);
      this.retryTimer = null;
    }
    this.ready.clear();
  }

  private emitStatus(): void {
    this.deps.onStatus?.(this.status());
  }
}
